// frontend/src/components/layout/NavLockedModal.tsx
// Brighella v2 — Wave 3d. Click-target for sidebar lock icons.
//
// SidebarNavGroup renders module/plan-gated children with a Lock icon.
// Clicking opens this modal, which explains what's locked and routes the
// admin to /settings/billing (non-admins get an "ask your admin" message
// instead of an actionable button).

import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LockKeyhole, X } from 'lucide-react'
import { useLanguage } from '../../i18n/index.jsx'
import useAuthStore from '../../store/authStore.js'
import { getModuleGateMeta } from '../../config/featureModules.js'

interface NavLockedModalProps {
  open: boolean
  onClose: () => void
  navKey?: string | null
  moduleKey?: string | null
  lockReason?: string | null
}

export default function NavLockedModal({
  open,
  onClose,
  navKey,
  moduleKey,
  lockReason,
}: NavLockedModalProps) {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const isAdmin = useAuthStore(s => s.isAdmin?.() ?? false)

  useEffect(() => {
    if (!open) return undefined
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  const meta = getModuleGateMeta(moduleKey)
  const featureName = meta.label
    ? t(meta.label)
    : (navKey ? (t(navKey) || navKey) : t('upgradeFallbackFeature'))
  const featureDescription = meta.description ? t(meta.description) : ''
  const requiredPlan = meta.requiredPlan || 'Pro+'

  return (
    <div
      role="presentation"
      onClick={(e: React.MouseEvent<HTMLDivElement>) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 'var(--z-overlay)',
        background: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('navLockedTitle')}
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 16,
          padding: '2rem',
          maxWidth: 460,
          width: '90%',
          border: '1px solid var(--border-default)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close')}
          style={{
            position: 'absolute', top: 12, right: 12,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', padding: 4,
          }}
        >
          <X size={18} aria-hidden="true" />
        </button>

        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'var(--accent-dim)', color: 'var(--accent)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <LockKeyhole size={28} aria-hidden="true" />
        </div>

        <h2 style={{
          margin: '0 0 8px',
          fontSize: 'var(--text-xl)',
          fontWeight: 700,
          color: 'var(--text-primary)',
        }}>
          {t('navLockedTitle')}: {featureName}
        </h2>

        {featureDescription && (
          <p style={{
            margin: '0 0 12px',
            fontSize: 'var(--text-base)',
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
          }}>
            {featureDescription}
          </p>
        )}

        <p style={{
          margin: '0 0 20px',
          fontSize: 'var(--text-base)',
          color: 'var(--text-muted)',
          lineHeight: 1.6,
        }}>
          {lockReason === 'plan'
            ? (isAdmin
                ? t('navLockedPlanAdminBody', { requiredPlan })
                : t('navLockedPlanCrewBody', { requiredPlan }))
            : (isAdmin
                ? t('navLockedModuleAdminBody')
                : t('navLockedModuleCrewBody'))}
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px', borderRadius: 8,
              border: '1px solid var(--border-default)',
              background: 'transparent', color: 'var(--text-secondary)',
              fontSize: 'var(--text-base)', cursor: 'pointer',
            }}
          >
            {t('close')}
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                onClose()
                // Plan gates → billing page (upgrade tier). Module gates →
                // admin settings page where the module toggle lives.
                navigate(lockReason === 'plan' ? '/settings/billing' : '/admin')
              }}
              style={{
                padding: '8px 18px', borderRadius: 8,
                border: 'none', background: 'var(--accent)',
                color: 'var(--text-inverse)', fontSize: 'var(--text-base)',
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              {lockReason === 'plan' ? t('upgradePlan') : t('navLockedManageModules')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
